/* import __COLOCATED_TEMPLATE__ from './conditions-editor.hbs'; */
/* RESPONSIBLE TEAM: team-tickets-1 */
import Component from '@glimmer/component';
import { inject as service } from '@ember/service';
import { action } from '@ember/object';
import type ConversationAttributeDescriptor from 'embercom/models/conversation-attributes/descriptor';
import type Condition from 'embercom/models/conversation-attributes/condition';
import type ListOption from 'embercom/models/conversation-attributes/list-option';
import type Store from '@ember-data/store';
import type IntlService from 'ember-intl/services/intl';
import type IntercomConfirmService from 'embercom/services/intercom-confirm-service';
import { schedule } from '@ember/runloop';
import scrollIntoViewIfNotVisible from 'embercom/lib/scroll-into-view-if-not-visible';
import { tracked } from '@glimmer/tracking';

interface Args {
  targetAttribute: ConversationAttributeDescriptor;
  allAttributes: ConversationAttributeDescriptor[];
  isTicketDescriptor: boolean;
  onClose?: () => void;
  onSave?: () => void;
}

export default class ConditionsEditor extends Component<Args> {
  @service declare store: Store;
  @service declare intl: IntlService;
  @service declare intercomConfirmService: IntercomConfirmService;
  @tracked showUploadModal = false;

  @action availableDependentAttributes(condition: Condition) {
    let usedAttributes = this.args.targetAttribute.dependentConditions
      .rejectBy('isDeleted')
      .filterBy('controllingListOptionId', condition.controllingListOptionId)
      .mapBy('descriptorId');
    return this.args.allAttributes
      .rejectBy('isBuiltIn')
      .rejectBy('archived')
      .rejectBy('id', this.args.targetAttribute.id)
      .sortBy('order')
      .map((attribute) => {
        let additionalOptions = {};
        if (
          attribute.id !== condition.descriptorId?.toString() &&
          usedAttributes.includes(attribute.id)
        ) {
          additionalOptions = {
            isDisabled: true,
            tooltip: {
              text: this.intl.t(
                'settings.conversation-attributes.conditions-side-drawer.already-selected-error',
              ),
              isDelayed: false,
            },
          };
        }

        return {
          text: attribute.displayName,
          value: attribute.id,
          ...additionalOptions,
        };
      });
  }

  get visibleConditions() {
    return this.args.targetAttribute.dependentConditions
      .reject((condition: Condition) => condition.descriptor && condition.descriptor.archived)
      .filterBy('isDeleted', false);
  }

  get canAddCondition() {
    return this.visibleConditions.length < 100;
  }

  get helpArticleLinkId() {
    if (this.args.isTicketDescriptor) {
      return 10147816; // https://www.intercom.com/help/en/articles/10147816-conditional-ticket-attributes
    }

    return 6546210; // https://www.intercom.com/help/en/articles/6546210-create-and-use-conversation-data-attributes-cvdas-in-the-inbox#h_f27bec93e2
  }

  @action conditionalValues(condition: Condition) {
    if (!condition.descriptorId) {
      return [];
    }
    let descriptor = this.args.allAttributes.findBy('id', condition.descriptorId.toString());
    if (!descriptor || descriptor.dataType !== 'list') {
      return [];
    }

    return [
      {
        text: this.intl.t('settings.conversation-attributes.conditions-side-drawer.all-options'),
        value: '',
        isSelected: condition.descriptorListOptionIds.length === 0,
      },
    ].concat(
      descriptor.listOptions.rejectBy('archived').map((option: ListOption) => {
        return {
          text: option.label,
          value: option.listOptionId,
          isSelected: condition.descriptorListOptionIds.includes(option.listOptionId),
        };
      }),
    );
  }

  @action onAddCondition() {
    this.store.createRecord('conversation-attributes/condition', {
      controllingDescriptorId: this.args.targetAttribute.id,
      controllingListOptionId: undefined,
      descriptorListOptionIds: [],
      controllingDescriptor: this.args.targetAttribute,
    });
    schedule('afterRender', () => {
      scrollIntoViewIfNotVisible($('[data-condition]:last').get(0));
    });
  }

  @action onRemoveCondition(index: number) {
    this.args.targetAttribute.dependentConditions.objectAt(index).deleteRecord();
    this.args.targetAttribute.dependentConditions = this.args.targetAttribute.dependentConditions;
  }

  @action onSelectDependentAttribute(index: number, value: string) {
    let descriptor = this.args.allAttributes.findBy('id', value);
    // refresh conditions for dependent attribute
    let conditions = this.args.targetAttribute.dependentConditions;
    let oldCondition = conditions.objectAt(index);

    let newCondition = this.store.createRecord('conversation-attributes/condition', {
      controllingDescriptorId: oldCondition.controllingDescriptorId,
      controllingListOptionId: oldCondition.controllingListOptionId,
      descriptorId: value,
      descriptor,
      descriptorListOptionIds: [],
      controllingDescriptor: this.args.targetAttribute,
    });

    // replace the old condition with the new one
    let allConditions = conditions.toArray();
    oldCondition.deleteRecord();
    allConditions[index] = newCondition;

    this.args.targetAttribute.dependentConditions = allConditions;
  }

  @action onSelectControllingAttributeListOption(index: number, value: string) {
    this.args.targetAttribute.dependentConditions.objectAt(index).controllingListOptionId = value;
  }

  @action onSelectConditionalValues(index: number, options: string[]) {
    let currentValue = this.args.targetAttribute.dependentConditions
      .objectAt(index)
      .descriptorListOptionIds.toArray();
    if (options.includes('') && currentValue.length > 0) {
      this.args.targetAttribute.dependentConditions.objectAt(index).descriptorListOptionIds = [];
    } else {
      this.args.targetAttribute.dependentConditions.objectAt(index).descriptorListOptionIds =
        options.filter((option) => option !== '');
    }
  }

  get optionsToDescriptorMap() {
    return this.args.targetAttribute.dependentConditions.reduce(
      (acc: Record<string, any>, condition: Condition) => {
        if (!condition.descriptorId || !condition.controllingListOptionId) {
          return acc;
        }

        if (!acc[condition.controllingListOptionId]) {
          acc[condition.controllingListOptionId] = [];
        }

        acc[condition.controllingListOptionId].push(condition.descriptorId);
        return acc;
      },
      {} as Record<string, any>,
    );
  }

  @action controllingAttributeListOptionsItems(condition: Condition) {
    return this.args.targetAttribute.listOptions
      .filter((option: ListOption) => option.label.length > 0)
      .rejectBy('archived')
      .map((option: ListOption) => {
        let additionalOptions = {};
        if (
          option.listOptionId !== condition.controllingListOptionId &&
          this.optionsToDescriptorMap[option.listOptionId] &&
          this.optionsToDescriptorMap[option.listOptionId].includes(condition.descriptorId)
        ) {
          additionalOptions = {
            isDisabled: true,
            tooltip: {
              text: this.intl.t(
                'settings.conversation-attributes.conditions-side-drawer.already-selected-error',
              ),
              isDelayed: false,
            },
          };
        }

        return {
          text: option.label,
          value: option.listOptionId,
          ...additionalOptions,
        };
      });
  }

  @action controllingAttributeOption(condition: Condition) {
    let controllingListOption = this.args.targetAttribute.listOptions.findBy(
      'listOptionId',
      condition.controllingListOptionId,
    );
    if (!controllingListOption || !controllingListOption.label) {
      return undefined;
    }
    return condition.controllingListOptionId;
  }

  @action handleImportedConditions(
    conditions: Array<{
      controllingListOptionId: string;
      descriptorId: string;
      descriptorListOptionIds: string[];
    }>,
  ) {
    conditions.forEach((condition) => {
      let descriptor = this.args.allAttributes.findBy('id', condition.descriptorId);

      this.store.createRecord('conversation-attributes/condition', {
        controllingDescriptorId: this.args.targetAttribute.id,
        controllingListOptionId: condition.controllingListOptionId,
        descriptorId: condition.descriptorId,
        descriptorListOptionIds: condition.descriptorListOptionIds,
        controllingDescriptor: this.args.targetAttribute,
        descriptor,
      });
    });
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Settings::ConversationAttributes::ConditionsEditor': typeof ConditionsEditor;
    'settings/conversation-attributes/conditions-editor': typeof ConditionsEditor;
  }
}
